import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { monitoringAPI } from '../services/api';

const TIME_OPTIONS = [
  { label: '5m',  minutes: 5 },
  { label: '10m', minutes: 10 },
  { label: '30m', minutes: 30 },
  { label: '1h',  minutes: 60 },
  { label: '2h',  minutes: 120 },
  { label: '6h',  minutes: 360 },
  { label: '12h', minutes: 720 },
  { label: '24h', minutes: 1440 },
  { label: '48h', minutes: 2880 },
];

const SERIES_COLORS = ['#ffb300', '#42a5f5', '#ef5350', '#66bb6a', '#ab47bc', '#26c6da', '#8d6e63'];

function cleanMessage(value) {
  if (!value) return 'Unknown';
  return String(value).replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim() || 'Unknown';
}

function formatXAxisLabel(ts, minutesWindow) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  if (minutesWindow > 1440) {
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function AjaxFailuresPage() {
  const [minutes, setMinutes] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('__all__');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hours = minutes / 60;
      const res = await monitoringAPI.getAjaxFailures({ hours });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [minutes]);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = data?.buckets ?? [];
  const totalErrors = buckets.reduce((s, b) => s + b.total, 0);

  const { chartData, seriesKeys, totalsBySeries } = useMemo(() => {
    if (!buckets.length) {
      return { chartData: [], seriesKeys: [], totalsBySeries: [] };
    }

    const totalsMap = {};
    buckets.forEach((bucket) => {
      (bucket.failures || []).forEach((failure) => {
        const label = cleanMessage(failure.log_message);
        totalsMap[label] = (totalsMap[label] || 0) + Number(failure.qty || 0);
      });
    });

    const topKeys = Object.entries(totalsMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([key]) => key);

    const totals = topKeys.map((key) => ({ key, total: totalsMap[key] }));

    const sortedBuckets = [...buckets].sort(
      (a, b) => new Date(a.time_interval).getTime() - new Date(b.time_interval).getTime()
    );

    const points = sortedBuckets.map((bucket) => {
      const point = {
        time_interval: bucket.time_interval,
        xLabel: formatXAxisLabel(bucket.time_interval, minutes),
        total: Number(bucket.total || 0),
      };

      topKeys.forEach((key) => {
        point[key] = 0;
      });
      point.Other = 0;

      (bucket.failures || []).forEach((failure) => {
        const label = cleanMessage(failure.log_message);
        const qty = Number(failure.qty || 0);
        if (topKeys.includes(label)) {
          point[label] += qty;
        } else {
          point.Other += qty;
        }
      });

      return point;
    });

    const hasOther = points.some((p) => p.Other > 0);
    const keys = hasOther ? [...topKeys, 'Other'] : topKeys;
    if (hasOther) {
      totals.push({ key: 'Other', total: points.reduce((s, p) => s + p.Other, 0) });
    }

    return { chartData: points, seriesKeys: keys, totalsBySeries: totals };
  }, [buckets, minutes]);

  useEffect(() => {
    if (selectedMetric !== '__all__' && !seriesKeys.includes(selectedMetric)) {
      setSelectedMetric('__all__');
    }
  }, [selectedMetric, seriesKeys]);

  const visibleSeriesKeys = selectedMetric === '__all__'
    ? seriesKeys
    : seriesKeys.filter((k) => k === selectedMetric);

  const visibleTotals = selectedMetric === '__all__'
    ? totalsBySeries
    : totalsBySeries.filter((t) => t.key === selectedMetric);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Ajax Failures
          </Typography>
          {data && (
            <Typography variant="caption" color="text.secondary">
              UTC window: {data.from_dt} to {data.to_dt} · {buckets.length} buckets · {totalErrors} errors · rendered in your local timezone
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="ajax-failures-metric-label">Metric</InputLabel>
            <Select
              labelId="ajax-failures-metric-label"
              value={selectedMetric}
              label="Metric"
              onChange={(e) => setSelectedMetric(e.target.value)}
            >
              <MenuItem value="__all__">All metrics</MenuItem>
              {seriesKeys.map((key) => (
                <MenuItem key={key} value={key}>{key}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={minutes}
            onChange={(_, v) => v && setMinutes(v)}
          >
            {TIME_OPTIONS.map((o) => (
              <ToggleButton key={o.label} value={o.minutes} sx={{ px: 1.5, fontSize: 12 }}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* States */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !error && buckets.length === 0 && (
        <Alert severity="info">No errors in the selected time range.</Alert>
      )}

      {/* Stacked graph */}
      {!loading && buckets.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Stacked error trends by 5-minute bucket (top categories + Other)
          </Typography>

          <Box sx={{ height: { xs: 360, md: 480 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
                <XAxis
                  dataKey="xLabel"
                  minTickGap={24}
                  tick={{ fontSize: 12, fill: '#bdbdbd' }}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#bdbdbd' }}
                />
                <RechartsTooltip
                  formatter={(value, name) => [value, name]}
                  labelFormatter={(_, payload) => {
                    const ts = payload?.[0]?.payload?.time_interval;
                    if (!ts) return '';
                    return new Date(ts).toLocaleString();
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />

                {visibleSeriesKeys.map((key, idx) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stackId="errors"
                    stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                    fill={SERIES_COLORS[idx % SERIES_COLORS.length]}
                    fillOpacity={0.45}
                    strokeWidth={1.5}
                    isAnimationActive={false}
                  />
                ))}

                <Line
                  type="monotone"
                  dataKey="total"
                  name="Total"
                  stroke="#ffffff"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  hide={selectedMetric !== '__all__'}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {visibleTotals.map((item) => (
              <Chip key={item.key} size="small" label={`${item.key}: ${item.total}`} variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
